import { useCallback, useMemo, useRef } from "react";

import { useLocalStorage } from "@/lib/react-use/use-local-storage";

import { pruneReadHrefs } from "./prune-read-hrefs";
import { STORAGE_KEY } from "./storage-key";

/**
 * ホスト管理画面のニュース既読状態をローカルストレージで管理する。
 * 既読は端末/ブラウザ単位の管理で、サーバには保持しない。
 *
 * `existingHrefs` を渡すと、現存しないニュースの既読履歴を剪定する。
 * 両側に存在する href は値を維持する。
 *
 * 注: existingHrefs は support サイトから実行時に取得するため、到着前は undefined になる。
 * undefined の間は剪定しない（空配列で剪定すると既読が全消しになる）。到着後に 1 回だけ剪定する。
 */
export const useNewsReadState = (existingHrefs: readonly string[] | undefined) => {
	const [storedHrefs, setStoredHrefs] = useLocalStorage<string[]>(STORAGE_KEY, []);
	// useLocalStorage は undefined を返す可能性があるため空配列にフォールバック。
	// useCallback の依存安定化のため useMemo で同一参照を維持する。
	const readHrefs = useMemo(() => storedHrefs ?? [], [storedHrefs]);

	// existingHrefs が届いた後に 1 回だけ、現存しないニュースの既読履歴を剪定する。
	// 描画用の派生値ではなく永続データそのものを整理する処理のため、レンダー中の setter で表現する。
	const hasPrunedRef = useRef(false);
	if (existingHrefs && !hasPrunedRef.current) {
		hasPrunedRef.current = true;
		const pruned = pruneReadHrefs(readHrefs, existingHrefs);
		if (pruned) setStoredHrefs(pruned);
	}

	const isRead = useCallback((href: string) => readHrefs.includes(href), [readHrefs]);

	const markAsRead = useCallback(
		(href: string) => {
			if (readHrefs.includes(href)) return;
			setStoredHrefs([...readHrefs, href]);
		},
		[readHrefs, setStoredHrefs],
	);

	return { isRead, markAsRead };
};
